import React, { forwardRef } from 'react';
import { Eye, EyeOff, AlertCircle, Search, CheckCircle2 } from 'lucide-react';
import { InputProps } from './types';

export const Input = forwardRef<HTMLInputElement, InputProps>(
	(
		{
			size = 'medium',
			label,
			error,
			success,
			hint,
			prefix,
			suffix,
			allowClear = false,
			isPassword = false,
			className = '',
			wrapperClassName = '',
			disabled = false,
			...props
		},
		ref
	) => {
		// State for password visibility
		const [showPassword, setShowPassword] = React.useState(false);
		// State for input value (for allowClear functionality)
		const [value, setValue] = React.useState(
			props.value || props.defaultValue || ''
		);

		// Size classes mapping
		const sizeClasses = {
			small: 'h-8 text-sm px-2',
			medium: 'h-10 text-base px-3',
			large: 'h-12 text-lg px-4'
		};

		// Base classes for the input
		const baseInputClasses = `
    w-full
    rounded-md
    border
    border-gray-300
    bg-white
    transition-all
    duration-200
    placeholder:text-gray-400
    disabled:bg-gray-50
    disabled:text-gray-500
    disabled:cursor-not-allowed
    focus:outline-none
    focus:ring-2
    focus:ring-blue-500
    focus:border-transparent
    ${sizeClasses[size]}
    ${error ? 'border-red-500' : 'focus:border-blue-500'}
    ${success && !error ? 'border-green-500' : 'focus:border-blue-500'}
  `;

		// Handle value change for allowClear functionality
		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			setValue(e.target.value);
			props.onChange?.(e);
		};

		// Handle clear button click
		const handleClear = () => {
			setValue('');
			const event = new Event('input', { bubbles: true });
			const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
				window.HTMLInputElement.prototype,
				'value'
			)?.set;
			const input = document.getElementById(
				props.id || ''
			) as HTMLInputElement;
			if (input && nativeInputValueSetter) {
				nativeInputValueSetter.call(input, '');
				input.dispatchEvent(event);
			}
		};

		// Toggle password visibility
		const togglePasswordVisibility = () => {
			setShowPassword(!showPassword);
		};

		return (
			<div className={`space-y-1 ${wrapperClassName}`}>
				{/* Label */}
				{label && (
					<label
						htmlFor={props.id}
						className='block text-sm font-medium text-gray-700'
					>
						{label}
					</label>
				)}

				{/* Input wrapper */}
				<div className='relative'>
					{/* Prefix */}
					{prefix && (
						<span className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400'>
							{prefix}
						</span>
					)}

					{/* Input */}
					<input
						{...props}
						ref={ref}
						type={
							isPassword
								? showPassword
									? 'text'
									: 'password'
								: props.type
						}
						value={value}
						onChange={handleChange}
						disabled={disabled}
						className={` ${baseInputClasses} ${prefix ? 'pl-10' : ''} ${suffix || allowClear || isPassword ? 'pr-10' : ''} ${className} `}
					/>

					{/* Suffix/Clear/Password toggle */}
					<div className='absolute right-3 top-1/2 flex -translate-y-1/2 items-center space-x-1'>
						{suffix && (
							<span className='text-gray-400'>{suffix}</span>
						)}

						{allowClear && value && (
							<button
								type='button'
								onClick={handleClear}
								className='text-gray-400 hover:text-gray-500 focus:outline-none'
							>
								<svg
									className='h-4 w-4'
									fill='none'
									stroke='currentColor'
									viewBox='0 0 24 24'
								>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth={2}
										d='M6 18L18 6M6 6l12 12'
									/>
								</svg>
							</button>
						)}

						{isPassword && (
							<button
								type='button'
								onClick={togglePasswordVisibility}
								className='text-gray-400 hover:text-gray-500 focus:outline-none'
							>
								{showPassword ? (
									<EyeOff className='h-4 w-4' />
								) : (
									<Eye className='h-4 w-4' />
								)}
							</button>
						)}
					</div>
				</div>

				{/* Error message */}
				{error && (
					<div className='flex items-center space-x-1 text-sm text-red-500'>
						<AlertCircle className='h-4 w-4' />
						<span>{error}</span>
					</div>
				)}

				{/* Error message */}
				{success && !error && (
					<div className='flex items-center space-x-1 text-sm text-green-500'>
						<CheckCircle2 className='h-4 w-4' />
						<span>{success}</span>
					</div>
				)}

				{/* Hint message */}
				{hint && !error && !success && (
					<p className='text-sm text-gray-500'>{hint}</p>
				)}
			</div>
		);
	}
);

Input.displayName = 'Input';

// Search Input variant
export const SearchInput = forwardRef<
	HTMLInputElement,
	Omit<InputProps, 'prefix' | 'isPassword'>
>((props, ref) => {
	return (
		<Input
			{...props}
			ref={ref}
			prefix={<Search className='h-4 w-4' />}
			allowClear={true}
		/>
	);
});

SearchInput.displayName = 'SearchInput';
